// src/components/Navbar.jsx
import * as React from "react";
import { Link } from "react-router-dom";
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "../components/ui/menubar";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetClose, SheetTrigger } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import {
  Menu,
  LogOut,
  User,
  Package,
  LayoutGrid,
  Sun,
  Moon,
  Home,
  Phone,
  BookOpen,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";

export default function Navbar({ toggleDarkMode, darkMode }) {
  const {
    user,
    userLoading,
    isAuthLoading,
    handleLogout,
    openLogin,
    openRegister,
    handleGoogleLogin,
  } = useAuth();

  const isLoggedIn = !!user?.username;

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">

          {/* Left Section - Logo + Dark Mode */}
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="flex items-baseline bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight">SE</span>
                <span className="text-xl md:text-2xl font-bold tracking-tight">RRA</span>
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight">ND</span>
              </div>
              <span className="text-sm md:text-base font-medium text-muted-foreground hidden lg:inline">
                | Plug Wa Kazi
              </span>
            </Link>

            <div className="relative inline-flex items-center scale-90 md:scale-100">
              <Switch
                checked={darkMode}
                onCheckedChange={toggleDarkMode}
                aria-label="Toggle dark mode"
                className="data-[state=checked]:bg-primary"
              />
              <span
                className={cn(
                  "absolute left-0.5 top-1/2 -translate-y-1/2 text-xs transition-all pointer-events-none",
                  darkMode ? "translate-x-5" : "translate-x-0"
                )}
              >
                {darkMode ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              </span>
            </div>
          </div>

          {/* ========== DESKTOP (≥768px) ========== */}
          <div className="hidden md:flex md:items-center md:gap-3 lg:gap-4">
            <Menubar className="border-none bg-transparent p-0">
              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/" className="flex items-center gap-1">
                    <Home className="h-3.5 w-3.5" />
                    Home
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>
              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/categories" className="flex items-center gap-1">
                    <LayoutGrid className="h-3.5 w-3.5" />
                    Categories
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>
              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/blog" className="flex items-center gap-1">
                    <BookOpen className="h-3.5 w-3.5" />
                    Blog
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>
              {isLoggedIn && (
                <>
                  <MenubarMenu>
                    <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                      <Link to="/profile" className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        Profile
                      </Link>
                    </MenubarTrigger>
                  </MenubarMenu>
                  <MenubarMenu>
                    <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                      <Link to="/manage-orders" className="flex items-center gap-1">
                        <Package className="h-3.5 w-3.5" />
                        Orders
                      </Link>
                    </MenubarTrigger>
                  </MenubarMenu>
                </>
              )}
              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/contact" className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    Contact
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>
            </Menubar>

            {/* Desktop Auth Buttons */}
            {userLoading || isAuthLoading ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : isLoggedIn ? (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium hidden xl:inline">
                  Jambo, <span className="text-foreground">{user.username}</span>
                </span>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut className="mr-1 h-4 w-4" />
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGoogleLogin}
                  className="gap-1"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>
                <Button variant="ghost" size="sm" onClick={openLogin}>
                  Login
                </Button>
                <Button variant="default" size="sm" onClick={openRegister}>
                  Register
                </Button>
              </div>
            )}
          </div>

          {/* ========== MOBILE (max-width: 767px) ========== */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Google Sign-In Button (only for anonymous users) */}
            {!userLoading && !isAuthLoading && !isLoggedIn && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleGoogleLogin}
                className="gap-1 h-8 px-2 text-xs"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Sign In</span>
              </Button>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[380px]">
                <div className="flex flex-col gap-6 py-8">
                  <SheetClose asChild>
                    <Link to="/" className="flex items-center gap-3 text-lg font-medium hover:text-primary">
                      <Home className="h-5 w-5" />
                      Home
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/categories" className="flex items-center gap-3 text-lg font-medium hover:text-primary">
                      <LayoutGrid className="h-5 w-5" />
                      Categories
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/blog" className="flex items-center gap-3 text-lg font-medium hover:text-primary">
                      <BookOpen className="h-5 w-5" />
                      Blog
                    </Link>
                  </SheetClose>
                  {isLoggedIn && (
                    <>
                      <SheetClose asChild>
                        <Link to="/profile" className="flex items-center gap-3 text-lg font-medium hover:text-primary">
                          <User className="h-5 w-5" />
                          Profile
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/manage-orders" className="flex items-center gap-3 text-lg font-medium hover:text-primary">
                          <Package className="h-5 w-5" />
                          Manage Orders
                        </Link>
                      </SheetClose>
                    </>
                  )}
                  <SheetClose asChild>
                    <Link to="/contact" className="flex items-center gap-3 text-lg font-medium hover:text-primary">
                      <Phone className="h-5 w-5" />
                      Contact
                    </Link>
                  </SheetClose>

                  <Separator className="my-4" />

                  {isLoggedIn ? (
                    <Button
                      variant="destructive"
                      onClick={handleLogout}
                      className="w-full justify-start gap-3"
                    >
                      <LogOut className="h-5 w-5" />
                      Logout
                    </Button>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <Button
                        variant="outline"
                        onClick={openLogin}
                        asChild
                        className="w-full justify-start"
                      >
                        <SheetClose>Login</SheetClose>
                      </Button>
                      <Button
                        variant="default"
                        onClick={openRegister}
                        asChild
                        className="w-full justify-start"
                      >
                        <SheetClose>Register</SheetClose>
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
